import { useState, useEffect, useCallback } from 'react';
import { Menu, X, Phone, ShoppingCart } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '../components/ui/sheet';

const LOGO_URL = "/images/cave-city-pawn-logo.png";

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'What We Carry', href: '#categories' },
  { label: 'Sell or Pawn', href: '#pawn-process' },
  { label: 'Archery', href: '#gun-range' },
  { label: 'About Us', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [open, setOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
    // Determine active section
    const sections = navLinks.map(l => l.href.slice(1));
    for (let i = sections.length - 1; i >= 0; i--) {
      const el = document.getElementById(sections[i]);
      if (el && el.getBoundingClientRect().top <= 120) {
        setActiveSection(sections[i]);
        break;
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-dirt-brown/95 backdrop-blur-sm shadow-deep py-2'
          : 'bg-dirt-brown/80 py-3'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Wood grain stripe at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-900 via-amber-700 to-amber-900 opacity-60" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <a href="#home" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
          <img
            src={LOGO_URL}
            alt="Cave City Pawn Shop & Trading Company Logo"
            className={`transition-all duration-300 ${scrolled ? 'h-10' : 'h-12'}`}
            width="auto"
            height={scrolled ? 40 : 48}
          />
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.href.slice(1)}`}
              className={`px-3 py-2 font-body font-semibold text-sm uppercase tracking-wider transition-colors duration-200 relative group ${
                activeSection === link.href.slice(1) ? 'text-accent' : 'text-bone-white hover:text-accent'
              }`}
            >
              {link.label}
              <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-accent transition-all duration-300 rounded-full ${
                activeSection === link.href.slice(1) ? 'w-3/4' : 'w-0 group-hover:w-3/4'
              }`} />
            </a>
          ))}
          <a
            href="https://alabamapawnandstorage.com/pawn-shop"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-shop-online-btn"
            className="ml-3 cave-btn px-5 py-2 bg-magma-red text-white font-heading text-sm uppercase tracking-wider rounded-rock shadow-deep hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 flex items-center gap-2"
          >
            <ShoppingCart size={16} />
            Shop Online
          </a>
          <a
            href="tel:850-615-6105"
            data-testid="nav-call-btn"
            className="ml-2 cave-btn px-4 py-2 border-2 border-accent text-accent font-heading text-sm uppercase rounded-rock hover:bg-accent hover:text-dirt-brown transition-colors duration-200 flex items-center gap-2"
          >
            <Phone size={14} />
            Call Now
          </a>
        </div>

        {/* Mobile Menu */}
        <div className="lg:hidden flex items-center gap-3">
          <a
            href="tel:850-615-6105"
            data-testid="nav-mobile-call"
            className="p-2 text-accent"
            aria-label="Call Cave City Pawn"
          >
            <Phone size={22} />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="p-2 text-bone-white"
                aria-label="Open menu"
              >
                <Menu size={26} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-dirt-brown border-l-4 border-amber-800 w-80">
              <div className="flex flex-col h-full pt-8">
                <img
                  src={LOGO_URL}
                  alt="Cave City Pawn Shop"
                  className="h-14 object-contain mb-8 self-center"
                />
                <div className="flex flex-col gap-1">
                  {navLinks.map((link) => (
                    <SheetClose asChild key={link.href}>
                      <a
                        href={link.href}
                        data-testid={`mobile-nav-${link.href.slice(1)}`}
                        className="px-4 py-3 text-bone-white font-body font-semibold text-lg uppercase tracking-wider hover:bg-amber-900/40 rounded-lg transition-colors"
                        onClick={() => setOpen(false)}
                      >
                        {link.label}
                      </a>
                    </SheetClose>
                  ))}
                </div>
                <div className="mt-auto pb-8 flex flex-col gap-3 px-4">
                  <a
                    href="https://alabamapawnandstorage.com/pawn-shop"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-shop-online-btn"
                    className="cave-btn text-center px-6 py-3 bg-magma-red text-white font-heading text-base uppercase tracking-wider rounded-rock shadow-deep"
                  >
                    Shop Online
                  </a>
                  <a
                    href="tel:850-615-6105"
                    data-testid="mobile-call-now-btn"
                    className="cave-btn text-center px-6 py-3 border-2 border-accent text-accent font-heading text-base uppercase rounded-rock"
                  >
                    Call 850-615-6105
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
